import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import SERVICES_DATA from "../pages/services/data";
import { BLOG_POSTS, getBlogPostBySlug } from "../pages/blog/data";

const SITE_URL = "https://vipreklamdekorasyon.com";
const DEFAULT_OG_IMAGE =
  "/images/127d7399-8f08-4ae5-b4d1-34aa8a0fa5a8_cropped-VIP_LOGO-removebg-preview-95b748ba.webp";

type MetaPayload = {
  title: string;
  description: string;
  keywords: string;
  image: string;
  type: "website" | "article";
  imageAlt?: string;
  robots?: string;
  canonicalPath?: string;
  jsonLd?: Record<string, unknown>;
};

const ROUTE_META: Record<string, Omit<MetaPayload, "type" | "image">> = {
  "/": {
    title: "Osmaniye Tabela Reklam Dekorasyon | VİP Reklam",
    description:
      "Osmaniye tabela, cephe kaplama, LED tabela, dekorasyon ve anahtar teslim proje hizmetlerinde 20+ yıllık tecrübe.",
    keywords:
      "osmaniye tabela, osmaniye reklam, cephe kaplama, led tabela, kutu harf tabela, reklam dekorasyon",
  },
  "/hizmetler": {
    title: "Hizmetlerimiz | VİP Reklam Dekorasyon",
    description:
      "Cephe kaplama, kutu harf tabela, totem tabela, cafe dekorasyonu, LED teknolojisi, matbaa ve anahtar teslim projeler.",
    keywords:
      "hizmetler, cephe kaplama, kutu harf, totem tabela, led teknolojisi, matbaa baskı",
  },
  "/projeler": {
    title: "Projelerimiz | VİP Reklam Dekorasyon",
    description:
      "Tamamlanan tabela, cephe kaplama, dekorasyon, LED ve baskı projelerimizi inceleyin.",
    keywords: "projeler, referans projeler, tabela projeleri, dekorasyon projeleri",
  },
  "/dekorasyon": {
    title: "Dekorasyon | VİP Reklam Dekorasyon",
    description:
      "Cafe, restoran, mağaza ve dış cephe dekorasyon uygulamalarımızı görsel galeri halinde inceleyin.",
    keywords: "dekorasyon, cafe dekorasyon, restoran dekorasyon, osmaniye dekorasyon, dekorasyon galeri",
  },
  "/referanslar": {
    title: "Referanslarımız | VİP Reklam Dekorasyon",
    description:
      "Osmaniye ve çevresinde birlikte çalıştığımız markalar ve kurumlar.",
    keywords: "referanslar, marka referansları, osmaniye reklam referans",
  },
  "/hakkimizda": {
    title: "Hakkımızda | VİP Reklam Dekorasyon",
    description:
      "2004'ten bu yana Osmaniye'de reklam, tabela, dekorasyon ve cephe kaplama alanlarında güvenilir çözüm ortağı.",
    keywords: "hakkımızda, vip reklam dekorasyon, osmaniye reklam firması",
  },
  "/iletisim": {
    title: "İletişim | VİP Reklam Dekorasyon",
    description:
      "VİP Reklam Dekorasyon iletişim bilgileri, telefon, adres ve WhatsApp hattı.",
    keywords: "iletişim, osmaniye reklam iletişim, vip reklam telefon",
  },
  "/blog": {
    title: "Blog | VİP Reklam Dekorasyon",
    description:
      "Tabela seçimi, dış cephe dekorasyon trendleri ve LED ekran teknolojileri hakkında güncel blog içerikleri.",
    keywords: "blog, tabela seçimi, dış cephe dekorasyon, led ekran, reklam trendleri",
  },
};

const STATIC_ROUTES = new Set(Object.keys(ROUTE_META));
const SERVICE_SLUGS = new Set(SERVICES_DATA.map((item) => item.slug));
const BLOG_SLUGS = new Set(BLOG_POSTS.map((post) => post.slug));

const monthMap: Record<string, string> = {
  ocak: "01",
  subat: "02",
  mart: "03",
  nisan: "04",
  mayis: "05",
  haziran: "06",
  temmuz: "07",
  agustos: "08",
  eylul: "09",
  ekim: "10",
  kasim: "11",
  aralik: "12",
};

const normalizeTurkish = (value: string) =>
  value
    .toLowerCase()
    .replace(/ı/g, "i")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ş/g, "s")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c");

const toIsoDate = (rawDate: string) => {
  const parts = normalizeTurkish(rawDate).split(" ");
  if (parts.length !== 3) return "2026-03-27";
  const [dayRaw, monthRaw, yearRaw] = parts;
  const day = dayRaw.padStart(2, "0");
  const month = monthMap[monthRaw] ?? "03";
  return `${yearRaw}-${month}-${day}`;
};

const buildBreadcrumb = (items: { name: string; path: string }[]) => ({
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: toAbsolute(item.path),
  })),
});

const orgJsonLd = {
  "@type": "Organization",
  name: "VİP Reklam Dekorasyon",
  url: SITE_URL,
  logo: toAbsolute(DEFAULT_OG_IMAGE),
  image: toAbsolute(DEFAULT_OG_IMAGE),
  sameAs: ["https://instagram.com/vip.tasarim"],
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: "+90-551-183-09-18",
      contactType: "customer service",
      areaServed: "TR",
      availableLanguage: ["tr"],
    },
  ],
};

const localBusinessJsonLd = {
  "@type": "LocalBusiness",
  name: "VİP Reklam Dekorasyon",
  image: toAbsolute(DEFAULT_OG_IMAGE),
  url: SITE_URL,
  telephone: "+90-551-183-09-18",
  priceRange: "$$",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Mehmet, Adnan Menderes, Mehmet Zahid Kotku Cd No: 36/1",
    postalCode: "80010",
    addressLocality: "Osmaniye Merkez",
    addressRegion: "Osmaniye",
    addressCountry: "TR",
  },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      opens: "08:00",
      closes: "18:00",
    },
  ],
};

function toAbsolute(url: string) {
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${SITE_URL}${url.startsWith("/") ? url : `/${url}`}`;
}

const setMetaTag = (selector: string, attr: "name" | "property", value: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, selector.includes('"') ? selector.split('"')[1] : "");
    document.head.appendChild(el);
  }
  el.content = value;
};

const setLinkTag = (rel: string, href: string) => {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
};

const removeJsonLd = () => {
  const existing = document.getElementById("vip-seo-jsonld");
  if (existing) existing.remove();
};

const setJsonLd = (data: Record<string, unknown>) => {
  const id = "vip-seo-jsonld";
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.text = JSON.stringify(data);
};

const SeoManager = () => {
  const { pathname } = useLocation();

  const payload = useMemo<MetaPayload>(() => {
    const cleanPath = pathname === "/" ? "/" : pathname.replace(/\/+$/, "");
    const serviceSlug = cleanPath.startsWith("/hizmetler/") ? cleanPath.replace("/hizmetler/", "") : "";
    const blogSlug = cleanPath.startsWith("/blog/") ? cleanPath.replace("/blog/", "") : "";

    if (cleanPath.startsWith("/hizmetler/") && !SERVICE_SLUGS.has(serviceSlug)) {
      return {
        title: "Sayfa Bulunamadı | VİP Reklam Dekorasyon",
        description: "Aradığınız hizmet sayfası bulunamadı. Güncel hizmetlerimizi inceleyebilirsiniz.",
        keywords: "404, hizmet sayfası bulunamadı, vip reklam dekorasyon",
        image: DEFAULT_OG_IMAGE,
        type: "website",
        robots: "noindex,follow",
        canonicalPath: "/hizmetler",
        jsonLd: {
          "@context": "https://schema.org",
          "@graph": [
            { "@type": "WebPage", name: "Sayfa Bulunamadı", url: toAbsolute("/hizmetler"), inLanguage: "tr-TR" },
            orgJsonLd,
          ],
        },
      };
    }

    if (cleanPath.startsWith("/blog/") && !BLOG_SLUGS.has(blogSlug)) {
      return {
        title: "Blog Yazısı Bulunamadı | VİP Reklam Dekorasyon",
        description: "Aradığınız blog yazısı bulunamadı. Güncel blog içeriklerimizi inceleyebilirsiniz.",
        keywords: "404, blog yazısı bulunamadı, vip reklam dekorasyon blog",
        image: DEFAULT_OG_IMAGE,
        type: "website",
        robots: "noindex,follow",
        canonicalPath: "/blog",
        jsonLd: {
          "@context": "https://schema.org",
          "@graph": [
            { "@type": "CollectionPage", name: "Blog", url: toAbsolute("/blog"), inLanguage: "tr-TR" },
            orgJsonLd,
          ],
        },
      };
    }

    if (pathname.startsWith("/hizmetler/")) {
      const slug = pathname.replace("/hizmetler/", "");
      const svc = SERVICES_DATA.find((item) => item.slug === slug);
      if (svc) {
        return {
          title: `${svc.title} | VİP Reklam Dekorasyon`,
          description: svc.metaDesc,
          keywords: `${svc.title.toLowerCase()}, osmaniye ${svc.shortTitle.toLowerCase()}, vip reklam dekorasyon`,
          image: svc.heroImg || DEFAULT_OG_IMAGE,
          type: "article",
          imageAlt: `${svc.title} hizmet görseli`,
          jsonLd: {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Service",
                name: svc.title,
                description: svc.description,
                provider: {
                  "@type": "Organization",
                  name: "VİP Reklam Dekorasyon",
                  url: SITE_URL,
                },
                areaServed: "Osmaniye",
                serviceType: svc.shortTitle,
                image: toAbsolute(svc.heroImg || DEFAULT_OG_IMAGE),
                url: `${SITE_URL}${pathname}`,
              },
              buildBreadcrumb([
                { name: "Ana Sayfa", path: "/" },
                { name: "Hizmetler", path: "/hizmetler" },
                { name: svc.shortTitle, path: pathname },
              ]),
              orgJsonLd,
            ],
          },
        };
      }
    }

    if (pathname.startsWith("/blog/")) {
      const slug = pathname.replace("/blog/", "");
      const post = getBlogPostBySlug(slug);
      if (post) {
        const isoDate = toIsoDate(post.date);
        return {
          title: `${post.title} | VİP Reklam Dekorasyon`,
          description: post.excerpt,
          keywords: `${post.title.toLowerCase()}, vip reklam blog, osmaniye reklam`,
          image: post.coverImg || DEFAULT_OG_IMAGE,
          type: "article",
          imageAlt: `${post.title} kapak görseli`,
          jsonLd: {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Article",
                headline: post.title,
                description: post.excerpt,
                image: toAbsolute(post.coverImg || DEFAULT_OG_IMAGE),
                datePublished: isoDate,
                dateModified: isoDate,
                inLanguage: "tr-TR",
                author: {
                  "@type": "Organization",
                  name: "VİP Reklam Dekorasyon",
                },
                publisher: {
                  "@type": "Organization",
                  name: "VİP Reklam Dekorasyon",
                  logo: {
                    "@type": "ImageObject",
                    url: toAbsolute(DEFAULT_OG_IMAGE),
                  },
                },
                mainEntityOfPage: `${SITE_URL}${pathname}`,
              },
              buildBreadcrumb([
                { name: "Ana Sayfa", path: "/" },
                { name: "Blog", path: "/blog" },
                { name: post.title, path: pathname },
              ]),
              orgJsonLd,
            ],
          },
        };
      }
    }

    const route = ROUTE_META[pathname] ?? ROUTE_META["/"];

    if (!STATIC_ROUTES.has(pathname)) {
      return {
        title: "Sayfa Bulunamadı | VİP Reklam Dekorasyon",
        description: "Aradığınız sayfa bulunamadı. Ana sayfadan tüm hizmetlerimize ulaşabilirsiniz.",
        keywords: "404, sayfa bulunamadı, vip reklam dekorasyon",
        image: DEFAULT_OG_IMAGE,
        type: "website",
        robots: "noindex,follow",
        canonicalPath: "/",
        jsonLd: {
          "@context": "https://schema.org",
          "@graph": [
            { "@type": "WebPage", name: "Sayfa Bulunamadı", url: SITE_URL, inLanguage: "tr-TR" },
            orgJsonLd,
          ],
        },
      };
    }

    const pageBreadcrumb =
      pathname === "/"
        ? buildBreadcrumb([{ name: "Ana Sayfa", path: "/" }])
        : buildBreadcrumb([
            { name: "Ana Sayfa", path: "/" },
            { name: route.title.replace(" | VİP Reklam Dekorasyon", ""), path: pathname },
          ]);

    return {
      ...route,
      image: DEFAULT_OG_IMAGE,
      type: "website",
      jsonLd: {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "WebPage",
            name: route.title,
            description: route.description,
            url: `${SITE_URL}${pathname}`,
            inLanguage: "tr-TR",
          },
          ...(pathname === "/"
            ? [
                {
                  "@type": "WebSite",
                  name: "VİP Reklam Dekorasyon",
                  url: SITE_URL,
                  inLanguage: "tr-TR",
                },
                localBusinessJsonLd,
              ]
            : []),
          pageBreadcrumb,
          orgJsonLd,
        ],
      },
    };
  }, [pathname]);

  useEffect(() => {
    const canonicalPath = payload.canonicalPath ?? pathname;
    const canonical = `${SITE_URL}${canonicalPath === "/" ? "" : canonicalPath}`;
    const imageUrl = toAbsolute(payload.image);

    document.title = payload.title;
    document.documentElement.lang = "tr-TR";

    setMetaTag('meta[name="description"]', "name", payload.description);
    setMetaTag('meta[name="keywords"]', "name", payload.keywords);
    setMetaTag('meta[name="robots"]', "name", payload.robots ?? "index,follow,max-image-preview:large");
    setMetaTag('meta[name="author"]', "name", "VİP Reklam Dekorasyon");
    setMetaTag('meta[name="format-detection"]', "name", "telephone=yes");

    setMetaTag('meta[property="og:type"]', "property", payload.type);
    setMetaTag('meta[property="og:title"]', "property", payload.title);
    setMetaTag('meta[property="og:description"]', "property", payload.description);
    setMetaTag('meta[property="og:url"]', "property", canonical);
    setMetaTag('meta[property="og:image"]', "property", imageUrl);
    setMetaTag(
      'meta[property="og:image:alt"]',
      "property",
      payload.imageAlt ?? "VİP Reklam Dekorasyon hizmet görseli",
    );
    setMetaTag('meta[property="og:locale"]', "property", "tr_TR");
    setMetaTag('meta[property="og:site_name"]', "property", "VİP Reklam Dekorasyon");

    setMetaTag('meta[name="twitter:card"]', "name", "summary_large_image");
    setMetaTag('meta[name="twitter:title"]', "name", payload.title);
    setMetaTag('meta[name="twitter:description"]', "name", payload.description);
    setMetaTag('meta[name="twitter:image"]', "name", imageUrl);
    setMetaTag(
      'meta[name="twitter:image:alt"]',
      "name",
      payload.imageAlt ?? "VİP Reklam Dekorasyon hizmet görseli",
    );

    setLinkTag("canonical", canonical);
    setLinkTag("alternate", canonical);
    const alternate = document.head.querySelector('link[rel="alternate"]');
    if (alternate) alternate.setAttribute("hreflang", "tr-TR");

    if (payload.jsonLd) setJsonLd(payload.jsonLd);
    else removeJsonLd();
  }, [pathname, payload]);

  return null;
};

export default SeoManager;
